/**
 * The numbers and places that do not change from one incident to the next.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  FILL THESE IN. Every empty string prints as a rule to write on, so the
 *  page is usable either way — but a number written by hand at 2am is a
 *  number somebody had to know, which is the thing this page exists to stop.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Deliberately a file rather than a table in the database. These change every
 * few years, they are the same for every incident, and they want reviewing
 * when they change rather than editing under pressure — which is what a
 * committed file gets you and a form does not. The per-incident answers
 * (commander, tonight's channel) live on the incident record instead.
 *
 * The test applied to every line here: would somebody act on it in the first
 * hours, or is it something they cannot look up with the network down? A
 * number that fails both tests makes the useful ones harder to find.
 */

export interface RefEntry {
  label: string;
  /** Empty prints a write-in rule. */
  value: string;
  /** Second line, smaller — an address, or when to use this instead. */
  detail?: string;
}

/**
 * 911 is the right number for fire, EMS and police, and it is printed alone
 * and large. Everything under it is deliberately *not* 911: the calls that
 * saturate the line and the ones no dispatcher can help with.
 */
export const EMERGENCY: RefEntry[] = [
  // The numbers that keep 911 clear: downed trees, a gas smell with no fire,
  // a wellness check. Separate lines because they are separate agencies —
  // Millcreek is Unified Police and Unified Fire — and somebody looking for
  // one of them under stress should not have to read the label twice.
  { label: 'Police non-emergency', value: '801-840-4000' },
  { label: 'Fire non-emergency', value: '801-743-7200' },
  // Gas leaks and downed conductors are what kill people after a quake, and
  // neither is a 911-first call unless something is already burning.
  { label: 'Gas leak', value: '800-767-1689', detail: 'Enbridge' },
  { label: 'Downed line / outage', value: '877-508-5088', detail: 'Rocky Mountain Power' },
  { label: 'Water', value: '801-483-6700', detail: 'Salt Lake City Public Utilities' },
  { label: 'Sewer', value: '801-262-2907', detail: 'Mt Olympus Improvement District' },
  // National, uniform, and genuinely used after a disaster: generator carbon
  // monoxide, contaminated water, medication taken wrong in the dark.
  { label: 'Poison control', value: '800-222-1222' },
];

/**
 * The stake's line.
 *
 * Shaped like the ward leadership block rather than like the reference lists,
 * because it is the same kind of thing — a role, a person, a number — and two
 * blocks of people set two different ways on one page read as an accident.
 * Ward callings come from Steward; these do not, so they live here.
 */
export const STAKE: { role: string; name: string; phone: string | null }[] = [
  { role: 'President', name: 'Rollin Johnson', phone: '818-326-2257' },
  { role: '1st counselor', name: 'Tad May', phone: '801-638-7020' },
  { role: '2nd counselor', name: 'John Snow', phone: '801-831-8282' },
  { role: 'Comms', name: '', phone: null },
];

/**
 * Places, not phone numbers.
 *
 * You drive to an emergency room; calling one accomplishes nothing. What a
 * driver needs is the name, the address, and which one to pick.
 */
export const PLACES: RefEntry[] = [
  { label: 'Meetinghouse', value: '3400 S 1100 E' },
  { label: 'Stake center', value: '3153 S 900 E' },
  { label: 'Nearest ER', value: "St Mark's Hospital", detail: '1200 E 3900 S' },
  { label: "Children's ER", value: "Primary Children's",
    detail: '100 N Mario Capecchi Dr' },
];

/**
 * Days, not hours.
 *
 * Kept as its own block, and last, because printing these beside 911 implies
 * they are alternatives to it. FEMA's line does nothing until there is a
 * federal declaration; the Red Cross matters at day two.
 */
export const LATER: RefEntry[] = [
  { label: 'Utah 211', value: '', detail: 'shelter, food, mental health' },
  // Dialled as digits, remembered as letters: RED-CROS keys to 733-2767 (the
  // last S is the overflow every vanity number has). The digits lead because
  // reading letters off a keypad is exactly the wrong task for somebody
  // rattled, and the mnemonic follows for anyone who only knows it that way.
  { label: 'Red Cross', value: '800-733-2767', detail: '800-RED-CROSS' },
  // Between the local EOC and FEMA. Placed above FEMA because that is the
  // order the escalation actually runs in — state first, federal only once a
  // declaration exists.
  { label: 'Utah Emergency Mgmt', value: '801-965-4461',
    detail: 'state coordination and declarations' },
  { label: 'FEMA assistance', value: '', detail: 'only after a federal declaration' },
];

/**
 * The two habits that decide whether anyone reaches anyone.
 *
 * Printed as standing text rather than data because they are advice, not
 * numbers, and they are the same in every emergency.
 */
export const COMMS_RULES = [
  'Text, do not call. SMS gets through congested cells when voice will not.',
  'Local circuits fail before long distance. Route through the out-of-area contact below.',
];

/** Who everyone relays through when local calling fails. */
export const OUT_OF_AREA: RefEntry = { label: 'Out-of-area relay', value: '' };

/**
 * The ward callings printed on the info page, in the order they are printed.
 *
 * Matched against the `callings` string on a person, so the position names
 * must be LCR's. Holders and phone numbers come from Steward — this list only
 * decides which of them are worth a place on the page.
 */
export const LEADERSHIP_POSITIONS: { group: string; positions: string[] }[] = [
  {
    group: 'Bishopric',
    positions: ['Bishop', 'Bishopric First Counselor', 'Bishopric Second Counselor'],
  },
  {
    group: 'Elders Quorum',
    positions: ['Elders Quorum President', 'Elders Quorum First Counselor',
                'Elders Quorum Second Counselor'],
  },
  {
    group: 'Relief Society',
    positions: ['Relief Society President', 'Relief Society First Counselor',
                'Relief Society Second Counselor'],
  },
];

/** How a position reads on paper once the organization already names it. */
export const shortPosition = (p: string) =>
  p.replace(/^(Bishopric|Elders Quorum|Relief Society)\s+/, '')
    .replace(/^First Counselor$/, '1st counselor')
    .replace(/^Second Counselor$/, '2nd counselor')
    .replace(/^President$/, 'President');
